import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Country, State, LocalGovernmentArea } from '../../types/geography';
import { DocumentTextIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { storage } from '../../services/storage';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { useContentProtection, logScreenshotAttempt } from '../../hooks/useContentProtection';
import '../../styles/content-protection.css';

// Configure PDF.js worker - use local file from public directory
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface InlinePDFViewerProps {
  selectedCountry?: Country;
  selectedState?: State;
  selectedLga?: LocalGovernmentArea;
  reportType: string;
  downloadTierRequired?: 'gold' | 'platinum' | 'diamond'; // Optional: specify minimum tier for downloads
}

interface StorageFile {
  name: string;
  id: string;
  created_at: string;
  metadata: any;
}

const InlinePDFViewer: React.FC<InlinePDFViewerProps> = ({
  selectedCountry,
  selectedState,
  selectedLga,
  reportType,
  downloadTierRequired,
}) => {
  const { isAuthenticated, profile } = useAuth();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  const hasSubscription = isAuthenticated && profile?.subscription_tier;

  // Check if user has required tier for downloads
  const hasDownloadAccess = () => {
    if (!hasSubscription) return false;
    if (!downloadTierRequired) return true; // No restriction if not specified
    
    const tierHierarchy: Record<string, number> = {
      'gold': 1,
      'platinum': 2,
      'diamond': 3
    };
    
    const userTierLevel = tierHierarchy[profile?.subscription_tier as string] || 0;
    const requiredTierLevel = tierHierarchy[downloadTierRequired] || 0;
    
    return userTierLevel >= requiredTierLevel;
  };

  // Initialize content protection
  const watermarkText = 'DO NOT SHARE!';
  useContentProtection({
    watermarkText,
    detectScreenshot: true,
    disableRightClick: true,
    onScreenshotDetected: () => {
      if (profile?.id) {
        logScreenshotAttempt(profile.id, `report-${reportType}`);
      }
    }
  });

  // Map geography to storage path
  const getStoragePath = (): string | null => {
    const countryToPath: Record<string, string> = {
      'Nigeria': 'Africa/Nigeria',
      'Ghana': 'Africa/Ghana',
      'Kenya': 'Africa/Kenya',
      'South Africa': 'Africa/South Africa',
      'Egypt': 'Africa/Egypt',
      'Benin': 'Africa/Benin',
      'Canada': 'North America/Canada',
      'United States': 'North America/United States',
      'Mexico': 'North America/Mexico',
    };

    if (selectedLga && selectedState && selectedCountry) {
      const basePath = countryToPath[selectedCountry.name];
      if (basePath) {
        return `${reportType}/${basePath}/${selectedState.name}/${selectedLga.name}`;
      }
    } else if (selectedState && selectedCountry) {
      const basePath = countryToPath[selectedCountry.name];
      if (basePath) {
        return `${reportType}/${basePath}/${selectedState.name}`;
      }
    } else if (selectedCountry) {
      const basePath = countryToPath[selectedCountry.name];
      if (basePath) {
        return `${reportType}/${basePath}`;
      }
    }
    
    return null;
  };

  // Fetch and display the first PDF file
  useEffect(() => {
    const loadPDF = async () => {
      const path = getStoragePath();
      if (!path) {
        setSignedUrl(null);
        setFileName('');
        return;
      }

      setLoading(true);
      
      try {
        // List files in the directory
        const { data: files, error: listError } = await storage.listReports(path);

        if (listError || !files || files.length === 0) {
          setSignedUrl(null);
          setFileName('');
          setLoading(false);
          return;
        }

        // Filter PDF files
        const pdfFiles = files.filter((file: StorageFile) => 
          file.name.toLowerCase().endsWith('.pdf')
        );

        if (pdfFiles.length === 0) {
          setSignedUrl(null);
          setFileName('');
          setLoading(false);
          return;
        }

        // Get the first PDF file
        const firstPDF = pdfFiles[0];
        const fullPath = `${path}/${firstPDF.name}`;
        
        // Get signed URL
        const { data: urlData, error: urlError } = await storage.getSignedUrl(fullPath);

        if (urlError || !urlData) {
          toast.error('Failed to load report');
          setSignedUrl(null);
          setLoading(false);
          return;
        }

        setSignedUrl(urlData.signedUrl);
        setFileName(firstPDF.name);
      } catch (error) {
        console.error('Error loading PDF:', error);
        toast.error('Failed to load report');
        setSignedUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadPDF();
  }, [selectedCountry, selectedState, selectedLga, reportType]);

  // Reset page number when PDF changes
  useEffect(() => {
    setPageNumber(1);
    setNumPages(null);
  }, [signedUrl]);

  const handleDownload = async () => {
    if (!hasDownloadAccess()) {
      const tierName = downloadTierRequired ? downloadTierRequired.charAt(0).toUpperCase() + downloadTierRequired.slice(1) : 'subscription';
      toast.error(`Download requires ${downloadTierRequired ? `${tierName} tier or higher` : 'a subscription'}. Upgrade to access downloads.`);
      return;
    }

    const path = getStoragePath();
    if (!path || !fileName) return;

    setDownloading(true);
    const fullPath = `${path}/${fileName}`;

    try {
      const { data, error } = await storage.downloadReport(fullPath);

      if (error || !data) {
        toast.error('Failed to download report');
        return;
      }

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download started!');
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!selectedCountry) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400" />
        <p className="mt-4 text-gray-600 font-medium">
          Select a location to view the report
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-gray-50 rounded-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading report...</span>
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400" />
        <p className="mt-4 text-gray-600 font-medium">No report available</p>
        <p className="text-sm text-gray-500 mt-2">
          {getStoragePath() ? `No files found for this location` : 'Storage path not configured'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with file info and download button */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3">
          <DocumentTextIcon className="h-6 w-6 text-primary-600" />
          <div>
            <p className="font-medium text-gray-900">{fileName}</p>
            <p className="text-sm text-gray-500">
              {selectedLga?.name || selectedState?.name || selectedCountry?.name}
            </p>
          </div>
        </div>
        
        {hasDownloadAccess() ? (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Downloading...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Download
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() => {
              const tierName = downloadTierRequired ? downloadTierRequired.charAt(0).toUpperCase() + downloadTierRequired.slice(1) : 'subscription';
              toast.error(`Download requires ${downloadTierRequired ? `${tierName} tier or higher` : 'a subscription'}. Upgrade to access downloads.`);
            }}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed"
            title={`Download requires ${downloadTierRequired ? `${downloadTierRequired} tier or higher` : 'subscription'}`}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Download
            <svg className="h-4 w-4 ml-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      {/* PDF Viewer with Pagination */}
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden protected-content"
        onContextMenu={(e) => e.preventDefault()}
      >
        <Document
          file={signedUrl}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={(error) => {
            console.error('PDF load error:', error);
            toast.error('Failed to load PDF');
          }}
          loading={
            <div className="flex items-center justify-center py-32">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading PDF...</span>
            </div>
          }
          error={
            <div className="text-center py-32 text-red-600">
              <p>Failed to load PDF. Please try again.</p>
            </div>
          }
          className="flex flex-col items-center"
          options={{
            cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
            cMapPacked: true,
            standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
          }}
        >
          {/* Wrap Page in relative container for watermark positioning */}
          <div className="relative inline-block">
            {/* Watermark overlay - constrained to PDF page */}
            <div className="watermark-overlay-pdf">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="watermark-text-pdf">
                  {watermarkText}
                </div>
              ))}
            </div>
            <Page
              pageNumber={pageNumber}
              width={1000}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg"
            />
          </div>
        </Document>

        {/* Pagination Controls */}
        {numPages && numPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
              disabled={pageNumber <= 1}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {pageNumber} of {numPages}
            </span>
            <button
              onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
              disabled={pageNumber >= numPages}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}
      </div>

      {/* Subscription prompt for non-subscribers or insufficient tier */}
      {!hasDownloadAccess() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Viewing in preview mode</p>
              <p className="text-sm text-yellow-700 mt-1">
                Subscribe to Gold, Platinum, or Diamond tier to download reports.{' '}
                <a href="/subscription" className="underline font-medium hover:text-yellow-900">
                  View plans
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlinePDFViewer;
